import { cn } from "@/lib/cn";
import type React from "react";

export interface ToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function Toggle({
  checked,
  onCheckedChange,
  disabled = false,
  id,
  label,
  className,
  "data-testid": testId,
}: ToggleProps) {
  const inputId = id ?? `toggle-${label ?? Math.random().toString(36).slice(2)}`;
  return (
    <label
      htmlFor={inputId}
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-3",
        "min-h-[44px] cursor-pointer select-none",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <span className="relative inline-flex h-7 w-12 items-center">
        <input
          id={inputId}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="peer sr-only"
        />
        <span
          className={cn(
            "absolute inset-0 rounded-full transition-colors duration-200 ease-out",
            "bg-border peer-checked:bg-accent",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2",
          )}
          aria-hidden="true"
        />
        <span
          className={cn(
            "absolute left-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out",
            checked ? "translate-x-5" : "translate-x-0",
          )}
          aria-hidden="true"
        />
      </span>
      {label && <span className="text-sm font-medium text-text">{label}</span>}
    </label>
  );
}
